import { ConnextSystem, MonitoringType, ProductModelService, VirtualSystem } from "../models";
import { Device } from "../models/firebase.model";
import { Series , Range } from "../telemetry";
import { getAggregateType, getRangeDataV2Type } from "../utils";
import { getSerialAndAttributeName } from "./query-constructors-utils";

export const BuildEnergyStateQuery = (devices: Device[], range: Range, inverterGwType: MonitoringType): string => {
  const deviceQueries = devices.map((device) => {
    const { serial, attributeName } = getSerialAndAttributeName(device.id);
    const aggregateType = getAggregateType(device.type, inverterGwType);

    return `
      ${attributeName}: getAggregate(serial: "${serial}", series: ${Series.DAY} , range: ${
        JSON.stringify(range).replace(/"(\w+)":/g, '$1:')
      }, type: "${aggregateType}") {
        EG1
        EG2
        EG3
        EG4
        EG5
        EG6
        ET1
        ET2
        ET3
        ET4
        ET5
        ET6
        TS
        SK
        SOL_EG
      }
    `;
  });

  return `query { ${deviceQueries.join('')} }`;
};

export const BuildDeviceLastAggregateQuery = (devices: Device[], inverterGwType: MonitoringType): string => {
  const deviceQueries = devices.map((device) => {
    const { serial, attributeName } = getSerialAndAttributeName(device.id);
    const aggregateType = getAggregateType(device.type, inverterGwType);

    return `
      ${attributeName}: getLastAggregate(serial: "${serial}", series: ${Series.DAY}, type: "${aggregateType}") {
        BP
        BP1
        BP2
        P_B1
        P_B2
        P_DC_TOT
        ALERTS
        P_SELF
        P_B_AGGR
        P_EXT
        P_EXP
        P_LOAD
        V
        EG1
        EG2
        EG3
        EG4
        EG5
        EG6
        ET1
        ET2
        ET3
        ET4
        ET5
        ET6
        TS
        SK
        SOL_IRR
        SOL_TEMP
        SOL_EG
      }
    `;
  });

  return `query { ${deviceQueries.join('')} }`;
};

export const BuildDeviceStatusLastAggregateQuery = (devices: Device[], inverterGwType: MonitoringType): string => {
  const deviceQueries = devices.map((device) => {
    const { serial, attributeName } = getSerialAndAttributeName(device.id);
    const aggregateType = getAggregateType(device.type, inverterGwType);

    return `
      ${attributeName}: getLastAggregate(serial: "${serial}", series: ${Series.DAY}, type: "${aggregateType}") {
        TS
        GwId
        ALERTS
      }
    `;
  });

  return `query { ${deviceQueries.join('')} }`;
};

export const BuildPowerStateQuery = (devices: Device[], range: Range, inverterGwType: MonitoringType): string => {
  const deviceQueries = devices.map((device) => {
    const { serial, attributeName } = getSerialAndAttributeName(device.id);
    const aggregateType = getAggregateType(device.type, inverterGwType);

    return `
      ${attributeName}: getAggregate(serial: "${serial}", series: ${Series.DAY} , range: ${
        JSON.stringify(range).replace(/"(\w+)":/g, '$1:')
      }, type: "${aggregateType}") {
        TS
        BP
        BP1
        BP2
        P_B1
        P_B2
        P_DC_TOT
        P_SELF
        P_B_AGGR
        P_EXT
        P_EXP
        P_LOAD
        V
      }
    `;
  });

  return `query { ${deviceQueries.join('')} }`;
};

export const BuildDeviceRangeEnergiesQuery = (serial: string, range: Range, series: Series, uiSystem: VirtualSystem | ConnextSystem) => {
  const product = new ProductModelService(serial);
  let aggregateType;
  if (uiSystem instanceof ConnextSystem) {
    aggregateType = 'connext';
  } else if (product.type?.name) {
    aggregateType = getAggregateType(product.type?.name, 'Solarman');
  } else {
    throw new Error('Aggregate type not found');
  };

  const { serial: deviceId, attributeName } = getSerialAndAttributeName(product.id);

  return `
    ${attributeName}: getAggregate(serial: "${deviceId}",
      range: ${ JSON.stringify(range).replace(/"(\w+)":/g, '$1:') },
      series: ${ JSON.stringify(series).replace(/"(\w+)"/g, '$1') },
      type: "${aggregateType}") {
      EG1
      EG2
      EG3
      EG4
      EG5
      EG6
      TS
      SK
    }
  `;
};

export const BuildVirtualSystemRangePowerQuery = (devices: { id: string }[], range: Range): string => {
  const deviceQueries = devices.map((device) => {
    const { serial, attributeName } = getSerialAndAttributeName(device.id);
    return `
    ${attributeName}: getInverterDataByRangeV2(serial: "${serial}", range: ${ JSON.stringify(range).replace(/"(\w+)":/g, '$1:') }) {
      GwId
      ALERTS
      TS
      P_DC_TOT
      P_EXP
      P_LOAD
      P_SELF
      P_B_AGGR
      P_EXT
      P_B1
      P_B2
      BP
      BP1
      BP2
      V
    }
  `;
  });
  return `query { ${deviceQueries.join('')} }`;
};

export const BuildVirtualSystemRangeV2PowerQuery = (devices: { id: string }[], range: Range, gwType: MonitoringType): string => {
  const deviceQueries = devices.map((device) => {
    const { serial, attributeName } = getSerialAndAttributeName(device.id);
    return `
    ${attributeName}: getInverterDataByRangeV2(
      serial: "${serial}",
      range: ${ JSON.stringify(range).replace(/"(\w+)":/g, '$1:') }
      type: ${getRangeDataV2Type(new ProductModelService(serial).type?.name || 'inverter', gwType)}
    ) {
      GwId
      ALERTS
      ALERTS_ZCS
      TS
      P_DC_TOT
      P_EXP
      P_LOAD
      P_SELF
      P_B_AGGR
      P_EXT
      P_B1
      P_B2
      BP
      BP1
      BP2
      V
      SOL_IRR
      SOL_TEMP
    }
  `;
  });
  return `query { ${deviceQueries.join('')} }`;
};

export const BuildFullTelemetryQuery = (devices: { id: string }[], range: Range): string => {
  const deviceQueries = devices.map((device) => {
    const { serial, attributeName } = getSerialAndAttributeName(device.id);
    return `
    ${attributeName}: getInverterDataByRangeV2(serial: "${serial}", range: ${ JSON.stringify(range).replace(/"(\w+)":/g, '$1:') }) {
      GwId
      TS
      TZ
      FT1
      FC1
      FR
      P_B1
      P_B2
      P_EXT
      BT
      BC
      BP
      BP1
      BP2
      BT1
      P_SELF
      P_DC_TOT
      P_EXP
      P_LOAD
      MP1
      MP2
      EG1
      EG2
      EG3
      EG4
      ET1
      ET2
      ET3
      ET4
      EG5
      EG6
      ET5
      ET6
      P_B_AGGR
      V
    }
  `;
  });
  return `query { ${deviceQueries.join('')} }`;
}

export const BuildTelemetryStatusQuery = (devices: Device[]): string => {
  const deviceQueries = devices.map((device) => {
    const { serial, attributeName } = getSerialAndAttributeName(device.id);
    return `${attributeName}: getInverterData(serial: "${serial}") {
      TS
      GwId
    }`;
  });
  return `${deviceQueries.join('')}`;
};

export const DefaultInverterPowerStateQuery = `
  fragment inverterv2 on InverterV2 {
    GwId
    TS
    ALERTS
    P_B1
    P_B2
    P_DC_TOT
    P_SELF
    P_B_AGGR
    P_EXT
    P_EXP
    P_LOAD
    BP
    BP1
    BP2
  }
`;

export const DefaultInverterPowerStateQueryForBlock = `
  fragment inverterv2 on InverterV2 {
    GwId
    TS
    ALERTS
    P_B1
    P_B2
    P_DC_TOT
    P_SELF
    P_B_AGGR
    P_EXT
    P_EXP
    P_LOAD
    BP
    BP1
    BP2

    MP1
    MP2
    MP3
    MP4
    MP5
    MP6
    MP7
    MP8
    MP9
    MP10
    MP11
    MP12
    MT1
    MT2
    MT3
    MT4
    MT5
    MT6
    MT7
    MT8
    MT9
    MT10
    MT11
    MT12
    MC1
    MC2
    MC3
    MC4
    MC5
    MC6
    MC7
    MC8
    MC9
    MC10
    MC11
    MC12
    N_MPPT
    N_MAX_MPPT
    N_MAX_STRINGS

    SC1
    SC2
    SC3
    SC4
    SC5
    SC6
    SC7
    SC8
    SC9
    SC10
    SC11
    SC12
    SC13
    SC14
    SC15
    SC16
    SC17
    SC18
    SC19
    SC20
    SC21
    SC22
    SC23
    SC24

    TI
    TB1
    TB2
    FC1
    FC2
    FC3
    FT1
    FT2
    FT3
    FR
    BC1
    BC2
    BT1
    BT2
    BN1
    BN2
  }
`;

export const DefaultInverterEnergyAggregateQuery = `
fragment aggregatevaluestype on AggregateValuesType {
    SK
    EG1
    EG2
    EG3
    EG4
    EG5
    EG6
    ET1
    ET2
    ET3
    ET4
    ET5
    ET6
    BP1
    BP2
    BP
    P_B1
    P_B2
    P_DC_TOT
    P_B_AGGR
    P_EXT
    P_EXP
    P_LOAD
    TS
  }
`;

export const DefaultInverterEnergyStateQuery = `
  fragment inverter on Inverter {
    EG1
    EG2
    EG3
    EG4
    EG5
    EG6
    TS
  }
`;

export const DefaultInverterSnapshotEnergyStateQuery = `
  fragment inverter on Inverter {
    EG1
    EG2
    EG3
    EG4
    EG5
    EG6
    TS
    BP1
    BP2
    BP
    P_B1
    P_B2
    P_DC_TOT
    P_B_AGGR
    P_EXT
    P_EXP
    P_LOAD
  }
`;

export const DefaultLastAggregateStatusQuery = `
fragment aggregatevaluestype on AggregateValuesType {
    TS
    GwId
  }
`;

export const tsQuery = `
  fragment aggregatevaluestype on AggregateValuesType {
    TS
  }
`;

export const BuildVirtualSystemTsQuery = (devices: Device[], range: Range): string => {
  const deviceQueries = devices.map((device) => {
    const { serial, attributeName } = getSerialAndAttributeName(device.id);
    return `
    ${attributeName}: getAggregate(serial: "${serial}", range: ${ JSON.stringify(range).replace(/"(\w+)":/g, '$1:') }, series: ${Series.DAY}, type: "solarman") {
      TS
    }
  `;
  });
  return `query { ${deviceQueries.join('')} }`;
};

export const BuildDeviceFwInfoQuery = (serial: string, inverterGwType: MonitoringType ): string => {
  const product = new ProductModelService(serial);
  const { serial: deviceId, attributeName } = getSerialAndAttributeName(product.id);
  const aggregateType = getAggregateType(product.type?.name!, inverterGwType);

  return `query {
    ${attributeName}: getLastAggregate(serial: "${deviceId}", series: ${Series.DAY}, type: "${aggregateType}") {
      TS
      ARM
      DSPS
      DSPM
    }
  }`;
};

export const BuildDevicesStatusInfoQuery = (devices: Device[], inverterGwType: MonitoringType): string => {
  const deviceQueries = devices.map((device) => {
    const product = new ProductModelService(device.id);
    const { serial, attributeName } = getSerialAndAttributeName(product.id);
    const aggregateType = getAggregateType(product.type?.name!, inverterGwType);

    return `
      ${attributeName}: getLastAggregate(serial: "${serial}", series: ${Series.DAY}, type: "${aggregateType}") {
        TS
        ARM
        DSPS
        DSPM
        GwId
        ALERTS
        ALERTS_ZCS
      }
    `;
  });

  return `query { ${deviceQueries.join('')} }`;
}
